// collector/ids.js
//
// The identity of a collector tap (collector-app P1-C part C) — a leaf module
// with no store or network in it, so the action layer, the photo helper and
// phase 2's replay can all use it without importing each other.
//   - `newActionId()`: a v4 uuid, the server's idempotency key.
//   - `nextSeq()`: the phone's own counter, strictly increasing across taps
//     and, where storage allows, across reloads.
//   - `nextClientAt()`: the tap's time, ISO 8601 with Z, never earlier than
//     the previous tap's — the server orders a batch by (clientAt, seq), so a
//     phone clock stepped back must not reorder taps. (A clock that ran fast
//     is the server's to clamp: a clientAt after its "now" is read as now.)
// Both counters are per-device conveniences in localStorage, wrapped so a
// private window or blocked storage just falls back to memory.

const SEQ_KEY = 'poolit:collector:seq';
const CLIENT_AT_KEY = 'poolit:collector:clientAt';

let memorySeq = 0;
let memoryClientAt = 0;

function readNumber(key) {
  try {
    return Number(localStorage.getItem(key)) || 0;
  } catch {
    return 0;
  }
}

function writeNumber(key, value) {
  try {
    localStorage.setItem(key, String(value));
  } catch {
    /* private mode / blocked storage: memory carries on */
  }
}

/** A v4 uuid — the backend validates `uuid`, so no non-uuid fallback. */
export function newActionId() {
  const cryptoApi = globalThis.crypto;
  if (typeof cryptoApi?.randomUUID === 'function') return cryptoApi.randomUUID();
  const bytes = cryptoApi.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/** The next per-device sequence number. */
export function nextSeq() {
  memorySeq = Math.max(memorySeq, readNumber(SEQ_KEY)) + 1;
  writeNumber(SEQ_KEY, memorySeq);
  return memorySeq;
}

/** Now as ISO 8601 with Z, never before the previous tap's time. */
export function nextClientAt(now = Date.now()) {
  memoryClientAt = Math.max(memoryClientAt, readNumber(CLIENT_AT_KEY), now);
  writeNumber(CLIENT_AT_KEY, memoryClientAt);
  return new Date(memoryClientAt).toISOString();
}

/**
 * Never hand out a seq or a clientAt at or below what the phone already
 * stored (collector-app P2-B): localStorage can be wiped apart from
 * IndexedDB, and a restarted counter would put new taps BEFORE the queued
 * ones — the server orders a batch by (clientAt, seq).
 *
 * @param {{ seq?: number, clientAt?: number }} floor - the highest stored seq, and clientAt in ms
 */
export function seedCounters({ seq = 0, clientAt = 0 } = {}) {
  if (Number(seq) > Math.max(memorySeq, readNumber(SEQ_KEY))) {
    memorySeq = Number(seq);
    writeNumber(SEQ_KEY, memorySeq);
  }
  if (Number(clientAt) > Math.max(memoryClientAt, readNumber(CLIENT_AT_KEY))) {
    memoryClientAt = Number(clientAt);
    writeNumber(CLIENT_AT_KEY, memoryClientAt);
  }
}
